import { Alert, Button } from '@economic/taco';
import { Trans } from '@lingui/macro';
import useSwr from 'swr';
import { Form } from '~/app/shared/components/Form';
import payrollApiClient from '~/app/shared/util/api/payrollApiClient';
import { useState } from 'react';
import type { CompanyResponse } from '~/payment-cards-feature';
import { getCompanyInfoStepValidationSchema, treatPhoneNumber } from '../../utils/utils';
import { CannotValidateCompanyDialog } from './CannotValidateCompanyDialog';
import { CheckCVRSeNumberResponse, CompanyInfoStepState } from '../../types';
import { CompanyInfoFormFields } from './CompanyInfoFormFields';

const checkCvrOrSeNumberFetcher = (url: string) => payrollApiClient.get(url).then(response => response.data);

export const DialogContent = ({
    currentCompanyData,
    handleNext,
    onSettingsSaved,
}: {
    currentCompanyData: CompanyResponse;
    handleNext: () => void;
    onSettingsSaved: () => void;
}) => {
    const { model: currentCompanyDataModel } = currentCompanyData;
    const { callingCode: companyCallingCode, number: companyPhoneNumber } = treatPhoneNumber(currentCompanyDataModel.phone);
    const [showCompanyFailedToValidateDialog, setShowCompanyFailedToValidateDialog] = useState(false);

    const { data: checkCvrOrSeNumberData } = useSwr<CheckCVRSeNumberResponse>(
        `/onboarding/check-cvr-se-number?cvrSeNumber=${currentCompanyDataModel.cvr}`,
        checkCvrOrSeNumberFetcher
    );

    const formInitialData: CompanyInfoStepState = {
        cvrOrSENumber: currentCompanyDataModel.cvr,
        companyName: currentCompanyDataModel.name,
        emailForInvoicing: currentCompanyDataModel.invoiceToEmail,
        companyCallingCode,
        companyPhoneNumber,
        postCode: currentCompanyDataModel.zipCode,
        city: currentCompanyDataModel.city,
        address: currentCompanyDataModel.address1,
        contactPersonName: currentCompanyDataModel.contact,
        contactPersonCallingCode: '',
        contactPersonPhoneNumber: '',
        contactPersonEmail: currentCompanyDataModel.email,
    };

    const onSubmit = () => {
        if (checkCvrOrSeNumberData?.isValidCVR || checkCvrOrSeNumberData?.isValidSE) {
            handleNext();
        } else {
            setShowCompanyFailedToValidateDialog(true);
        }
    };

    return (
        <>
            <Alert state="information" className="mb-6">
                <Trans>
                    We were able to find some information about your company in e-conomic, check if it is correct and make sure
                    all fields are completed.
                </Trans>
            </Alert>

            <Form<CompanyInfoStepState>
                onSubmit={onSubmit}
                initialData={formInitialData}
                key="payroll-onboarding-step2-form"
                skipNotifications
                validationSchema={getCompanyInfoStepValidationSchema()}>
                <>
                    <CompanyInfoFormFields formInitialData={formInitialData} />
                    <div className="flex w-full justify-end">
                        <Button type="submit" appearance="primary">
                            <Trans>Next</Trans>
                        </Button>
                    </div>
                </>
            </Form>
            {showCompanyFailedToValidateDialog && (
                <CannotValidateCompanyDialog
                    currentCompanyData={currentCompanyData}
                    onSettingsSaved={() => {
                        onSettingsSaved();
                        setShowCompanyFailedToValidateDialog(false);
                    }}
                />
            )}
        </>
    );
};
